import {
  can,
  type ChatState,
  PermissionFlags,
  selectChannelTree,
  selectIsChannelMuted,
  selectIsUnread,
  selectPrivateChannels,
} from '@alternativamais/chat-core';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';

import { avisarErro } from '../../componentes/Avisos';
import { comboDe, pedir } from '../../chat/atalhos';
import { abrirCamada } from '../../chat/camadas';
import { useCliente } from '../../chat/contexto';
import { lerPreferencias } from '../../chat/preferencias';

/** Canais na ordem da lateral (os que dá para abrir), ou as DMs. */
function canaisNaOrdem(estado: ChatState, guildId: string | null): string[] {
  if (!guildId) return selectPrivateChannels(estado).map((canal) => canal.id);
  return selectChannelTree(estado, guildId)
    .flatMap((grupo) => grupo.channels)
    .filter(
      (canal) =>
        !canal.legacy_voice &&
        can(estado, canal.id, PermissionFlags.VIEW_CHANNEL),
    )
    .map((canal) => canal.id);
}

function emCampo(alvo: EventTarget | null): boolean {
  const elemento = alvo as HTMLElement | null;
  return (
    !!elemento &&
    (elemento.tagName === 'INPUT' ||
      elemento.tagName === 'TEXTAREA' ||
      elemento.tagName === 'SELECT' ||
      elemento.isContentEditable)
  );
}

/**
 * Atalhos de teclado do app (C33 e os criados em U9). Não valem com um modal
 * ou as configurações abertas; dentro de um campo, só os com Ctrl/Alt.
 */
export function useAtalhosGlobais(
  guildId: string | null,
  canalId: string | null,
) {
  const cliente = useCliente();
  const navegar = useNavigate();
  const onde = useRef({ guildId, canalId });
  useEffect(() => {
    onde.current = { guildId, canalId };
  });

  useEffect(() => {
    const ir = (id: string) => {
      const canal = cliente.store.getState().channels[id];
      if (canal) navegar(`/canais/${canal.guild_id ?? '@me'}/${id}`);
    };
    const tecla = (evento: KeyboardEvent) => {
      if (evento.defaultPrevented) return;
      const combo = comboDe(evento);
      if (!combo) return;
      if (document.querySelector('[aria-modal=true]')) return;
      const campo = emCampo(evento.target);
      if (campo && !/^(Ctrl|Alt)\+/.test(combo)) return;
      const estado = cliente.store.getState();
      const { guildId: servidor, canalId: canal } = onde.current;
      const feito = () => evento.preventDefault();

      const proprio = lerPreferencias().atalhos.find(
        (atalho) => atalho.teclas === combo,
      );
      if (proprio) {
        feito();
        if (proprio.acao === 'nao-perturbe') {
          const atual = estado.userSettings?.status ?? 'online';
          cliente
            .setStatus(atual === 'dnd' ? 'online' : 'dnd')
            .catch(avisarErro);
        } else if (proprio.acao === 'abrir-canal' && proprio.alvo)
          ir(proprio.alvo);
        else if (proprio.acao === 'caixa') pedir('caixa');
        else if (proprio.acao === 'marcar-lido' && canal)
          cliente.ack(canal).catch(() => undefined);
        return;
      }

      switch (combo) {
        case 'Ctrl+K':
          feito();
          abrirCamada({ tipo: 'troca-rapida' });
          return;
        case 'Ctrl+/':
          feito();
          abrirCamada({ tipo: 'atalhos' });
          return;
        case 'Ctrl+I':
          feito();
          pedir('caixa');
          return;
        case 'Ctrl+F':
          feito();
          pedir('buscar');
          return;
        case 'Ctrl+P':
          feito();
          pedir('fixadas');
          return;
        case 'Ctrl+U':
          feito();
          pedir('membros');
          return;
        case 'Ctrl+Shift+U':
          feito();
          pedir('enviar-arquivo');
          return;
        case 'Ctrl+E':
          feito();
          pedir('emojis');
          return;
        case 'Ctrl+Home':
          feito();
          pedir('ir-inicio');
          return;
        case 'Ctrl+Alt+←':
          feito();
          navegar(-1);
          return;
        case 'Alt+↑':
        case 'Alt+↓': {
          const lista = canaisNaOrdem(estado, servidor);
          if (!lista.length) return;
          feito();
          const atual = canal ? lista.indexOf(canal) : -1;
          const passo = combo === 'Alt+↑' ? -1 : 1;
          ir(lista[(atual + passo + lista.length) % lista.length]);
          return;
        }
        case 'Alt+Shift+↓': {
          const lista = canaisNaOrdem(estado, servidor);
          const atual = canal ? lista.indexOf(canal) : -1;
          const ordem = [
            ...lista.slice(atual + 1),
            ...lista.slice(0, atual + 1),
          ];
          const proximo = ordem.find(
            (id) =>
              id !== canal &&
              selectIsUnread(estado, id) &&
              !selectIsChannelMuted(estado, id),
          );
          if (proximo) {
            feito();
            ir(proximo);
          }
          return;
        }
        case 'Esc':
          if (canal && !campo && selectIsUnread(estado, canal)) {
            feito();
            cliente.ack(canal).catch(() => undefined);
          }
          return;
        case 'Shift+Esc': {
          if (!servidor) return;
          feito();
          for (const id of canaisNaOrdem(estado, servidor)) {
            if (selectIsUnread(estado, id))
              cliente.ack(id).catch(() => undefined);
          }
          return;
        }
      }
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [cliente, navegar]);
}
